import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { useT } from "@/lib/LanguageContext";
import { ASSETS } from "@/lib/content";
import { useTenant } from "@/lib/TenantContext";
import { api } from "@/lib/api";
import { isTenantHost, platformOrigin } from "@/lib/tenantHost";
import GoogleLoginButton from "@/lib/GoogleLoginButton";
import { adminHref, consoleReturnTarget, LOGIN_RETURN_PARAM } from "./adminPath";

// This page is mounted on BOTH doors: routes/adminRoutes.jsx (platform host) and
// tenant/TenantSite.jsx (subdomain + verified custom domain). Only the first is an
// origin Google knows.
//
// Google Identity Services validates the PAGE's JavaScript origin against the OAuth
// client's registered origins, and accepts no wildcard. Every tenant subdomain and every
// customer custom domain is therefore a distinct, permanently unregisterable origin:
// rendering GIS there produces "The given origin is not allowed for the given client ID"
// and a 403 on the button — i.e. a practitioner on their own site could not sign in AT
// ALL. The auth handoff (backend/auth_handoff.py + saas/AuthBridge.jsx) is the existing
// answer, already wired to the CUSTOMER door (public/ClientPortal.jsx); this is the same
// pattern on the ADMIN door, and the code below is deliberately its mirror image.
// ── Failure code → the sentence the practitioner reads ────────────────────────
// The backend answers a sign-in refusal with a stable code in `detail`
// (403 TENANT_SUSPENDED for a practice suspended by a platform operator; the auth bridge
// also reports invalid_code / expired_code through ?auth_error=). A single generic
// "Sign-in failed. Try again." for all of them told a suspended practitioner to do the
// one thing that cannot work.
//
// ONE table, exported: the tenant-host bridge below and the platform-host Google button
// (lib/GoogleLoginButton, which threads its own onError body) must map the same code to
// the same message. An unknown code degrades to the generic message — a code we do not
// recognise must never render as raw text.
export const LOGIN_ERROR_KEYS = {
  TENANT_SUSPENDED: "login.errorSuspended",
};

export const loginErrorKey = (code) =>
  LOGIN_ERROR_KEYS[String(code || "").trim().toUpperCase()] || "login.error";

const Login = () => {
  const { user, loading, refresh } = useAuth();
  const t = useT("admin");
  const navigate = useNavigate();
  const location = useLocation();
  const error = new URLSearchParams(location.search).get("error");
  // Where a successful sign-in goes: the console page that sent the practitioner here
  // (`?next=`, set by ProtectedRoute and validated by adminPath.consoleReturnTarget), else
  // the dashboard home.
  const destination =
    consoleReturnTarget(new URLSearchParams(location.search).get(LOGIN_RETURN_PARAM))
    || adminHref("/admin");
  // The CODE, not a boolean: the message depends on WHY sign-in failed.
  const [authErrorCode, setAuthErrorCode] = React.useState(null);
  // BUG-041: a non-payment-suspended owner's sign-in still SUCCEEDS (billing must stay
  // reachable — see tenant_middleware.tenant_login_blocked); this is what tells them so,
  // read straight off the POST /auth/session response instead of staying silent until
  // AdminLayout's own /saas/my-tenant fetch happens to resolve.
  const [suspensionNotice, setSuspensionNotice] = React.useState(null);
  const { tenant } = useTenant();

  // ── Whose logo is this? ───────────────────────────────────────────────────
  // This screen used to render one fixed practice's real logo on every host, so a
  // practitioner's first login showed another business's identity. The branding shown
  // here must belong to the practice actually being signed into, or to nobody.
  //
  // The tenant in context cannot be trusted on its own: App.js wraps the whole app in
  // <TenantProvider slug="emilie">, so on the platform apex `tenant` is the SEED tenant
  // rather than the visitor's. It is only authoritative where the tenant was resolved
  // from something the visitor actually addressed — the Host (tenant subdomain / verified
  // custom domain, resolved by TenantSiteHost), or an explicit /t/:slug/ path prefix,
  // which is the other door TenantSite is mounted behind.
  //
  // Anywhere else we fall back to ASSETS.logo, which is a neutral placeholder mark that
  // names no one. Being generic is the safe failure; being wrong is not.
  const tenantResolvedFromRequest =
    isTenantHost() || /^\/t\/[^/]+(\/|$)/.test(location.pathname);
  const brandName = tenantResolvedFromRequest ? tenant?.name || "" : "";
  const brandLogo = (tenantResolvedFromRequest && tenant?.branding?.logo_url) || ASSETS.logo;

  React.useEffect(() => {
    if (!loading && user) {
      navigate(destination, { replace: true });
    }
  }, [user, loading, navigate, destination]);

  // The Google button hands us the whole POST /auth/session body (see
  // GoogleLoginButton.jsx's `onSuccessRef.current?.(data)`) — before this fix it was
  // discarded, so a suspended-for-non-payment owner was navigated to /admin having been
  // told nothing (BUG-041). `status`/`suspended_reason` only ever arrive here for the
  // non-payment case: a SANCTIONED practice never reaches a successful sign-in at all
  // (server.py refuses it with 403 TENANT_SUSPENDED, handled by handleSignInError below).
  //
  // A first fix rendered the notice below and then navigated to /admin in the same
  // synchronous handler; React's automatic batching commits both together, so the notice
  // shared at most one paint with the route swap before /admin — a page with no knowledge
  // of the suspension — replaced it. That is not an explanation, it's a flash.
  //
  // The actual fix is the DESTINATION: a non-payment-suspended owner lands on
  // /admin/billing instead of /admin. /api/tenant/billing/* stays reachable while
  // suspended (tenant_middleware.tenant_login_blocked only blocks LOGIN, not billing
  // reads/writes — see test_b97_tenant_suspension_login_gate.py), and that page already
  // renders the durable dunning copy (bill.dunning.suspendedTitle/Body in
  // BillingAdmin.jsx) plus AdminLayout's own AccountStatusBanner on every admin route
  // after. The notice below still renders for the one frame before that route swap — it
  // fetches nothing and reuses AccountStatusBanner's own wording (acct.suspended.text /
  // acct.billingCta), so it is not a second phrasing, and unlike AdminLayout's banner
  // (which waits on its own GET /saas/my-tenant round trip) it is already in hand the
  // instant sign-in succeeds — a real head start on a slow connection, not dead code.
  const handleSignedIn = (data) => {
    if (data?.status === "suspended" && data?.suspended_reason === "non_payment") {
      setSuspensionNotice({ status: data.status, reason: data.suspended_reason });
      navigate(adminHref("/admin/billing"), { replace: true });
      return;
    }
    navigate(destination, { replace: true });
  };

  // The Google button hands us the axios error, response body included, so the SAME table
  // that phrases the bridge's ?auth_error= codes phrases the direct sign-in refusals:
  // POST /auth/session answers 403 {"detail":"TENANT_SUSPENDED"} for a practice suspended by
  // a platform operator. "signin_failed" is the no-response case (network/popup) and reads as
  // the generic message.
  const handleSignInError = (e) => {
    setAuthErrorCode(e?.response?.data?.detail || "signin_failed");
  };

  // ── Auth bridge (tenant hosts) ────────────────────────────────────────────
  // Single-run guard: the hb_code is single-use server-side (find_one_and_delete), and
  // React StrictMode double-invokes this effect in dev — the second POST /finalize would
  // race the first with the nonce already removed and cancel it.
  const finalizedRef = React.useRef(false);
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    // consume() reports its own failures through auth_error (today it sends them to
    // /compte; reading the parameter here keeps this door correct the moment that
    // redirect follows the stored `next` instead).
    const err = params.get("auth_error");
    const finalize = params.get("hb_finalize");
    const strip = () => {
      params.delete("auth_error"); params.delete("hb_finalize");
      const qs = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
    };
    if (err) { setAuthErrorCode(err); strip(); return; }
    if (finalize) {
      if (finalizedRef.current) return;
      finalizedRef.current = true;
      const nonce = sessionStorage.getItem("hb_nonce");
      sessionStorage.removeItem("hb_nonce");
      (async () => {
        try {
          await api.post("/auth/handoff/finalize", { nonce: nonce || "" });
          await refresh();   // finalize sets the session cookie; the effect above routes to /admin
        } catch (e) {
          // finalize answers 403 {"detail":"TENANT_SUSPENDED"} when the target practice is
          // suspended by an operator, and 400 {"detail":"invalid_code"} for a missing or
          // wrong nonce (forged link). The code carries which one; "finalize_failed" is
          // the no-response case (network/offline) and reads as the generic message.
          setAuthErrorCode(e?.response?.data?.detail || "finalize_failed");
        } finally {
          strip();
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const bridgeSignIn = () => {
    const nonce = window.crypto?.randomUUID?.() || Math.random().toString(36).slice(2);
    // sessionStorage (tenant origin, THIS tab): the unforgeable proof that this browser
    // initiated the flow. Required at /finalize — anti login-CSRF.
    sessionStorage.setItem("hb_nonce", nonce);
    const u = new URL(window.location.href);
    u.searchParams.set("hb_nonce", nonce);
    // Return target = this page (/admin/login, its `?next=` included: the bridge keeps the
    // query string). Once finalize installs the session, the `user` effect above sends the
    // practitioner to that page, or to /admin when there is none.
    window.location.assign(`${platformOrigin()}/auth/bridge?return=${encodeURIComponent(u.toString())}`);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-eh-bg px-6">
      <div data-testid="admin-login-card" className="max-w-md w-full bg-eh-cream rounded-3xl border border-eh-border-soft p-10 shadow-[0_24px_60px_-25px_rgba(44,53,49,0.2)]">
        <img
          src={brandLogo}
          alt={brandName || "Logo"}
          data-testid="admin-login-logo"
          className="h-20 w-auto mx-auto"
        />
        <h1 className="font-display text-3xl text-eh-ink mt-6 text-center leading-tight">
          {t("login.title")}
        </h1>
        {/* Name the practice being signed into. Without it, a practitioner sent to a
            login screen has no way to tell WHICH dashboard the credentials will open. */}
        {brandName && (
          <p data-testid="admin-login-tenant" className="text-eh-ink text-center mt-2 font-medium">
            {brandName}
          </p>
        )}
        <p className="text-eh-muted text-center mt-2">{t("login.subtitle")}</p>
        {(error || authErrorCode) && (
          <div data-testid="admin-login-error" className="mt-5 text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-2 text-center">
            {t(loginErrorKey(error || authErrorCode))}
          </div>
        )}
        {/* Same wording as admin/AdminLayout.jsx's AccountStatusBanner — never a second
            phrasing of the same fact (BUG-041). Rendered here so the explanation is present
            the instant sign-in succeeds, not only once the console's own tenant fetch
            resolves. */}
        {suspensionNotice && (
          <div
            data-testid="admin-login-suspended-notice"
            className="mt-5 text-sm text-rose-900 bg-rose-100 border border-rose-300 rounded-xl px-4 py-3 text-center"
          >
            <p>{t("acct.suspended.text")}</p>
            <a href={adminHref("/admin/billing")} data-testid="admin-login-billing-link" className="font-semibold underline">
              {t("acct.billingCta")}
            </a>
          </div>
        )}
        <div className="mt-8" data-testid="admin-google-signin">
          {isTenantHost() ? (
            <button
              onClick={bridgeSignIn}
              data-testid="admin-bridge-signin"
              className="inline-flex items-center justify-center gap-2 w-full px-6 py-3 rounded-full bg-eh-ink text-eh-cream hover:bg-eh-ink/90 transition text-sm font-medium"
            >
              {t("login.continueGoogle")}
            </button>
          ) : (
            <GoogleLoginButton onSuccess={handleSignedIn} onError={handleSignInError} />
          )}
        </div>
        <p className="mt-6 text-xs text-center text-eh-muted">
          {t("login.restricted")}
        </p>
      </div>
    </div>
  );
};

export default Login;
